import React, { useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { ZoomIn, ZoomOut, RefreshCw, Download, FileText, FileJson, Upload, Moon, Sun, Menu, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import NotebookMenu from './notebook/NotebookMenu';
import { Stage, UserJourney } from '@/types/journey';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useThemeContext } from './ThemeProvider';
import BuyMeCoffeeButton from './BuyMeCoffeeButton';

interface HeaderProps {
  title: string;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onReset: () => void;
  onExport: () => void;
  onExportPDF: () => void;
  onExportJSON: () => void;
  onImport: () => void;
  onSave: () => void;
  onCreateNew: () => void;
  setStages: React.Dispatch<React.SetStateAction<Stage[]>>;
  setJourney: React.Dispatch<React.SetStateAction<UserJourney>>;
  zoomLevel: number;
  stages: Stage[];
  journey: UserJourney;
  isMobile?: boolean;
}

const Header: React.FC<HeaderProps> = ({ 
  title, 
  onZoomIn, 
  onZoomOut, 
  onReset, 
  onExport, 
  onExportPDF,
  onExportJSON,
  onImport,
  onSave,
  onCreateNew,
  setStages,
  setJourney,
  zoomLevel,
  stages,
  journey,
  isMobile
}) => {
  const { theme, setTheme } = useThemeContext();
  
  // Add keyboard shortcut for saving
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Cmd/Ctrl+S
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault(); // Prevent browser's save dialog
        
        // Only trigger the global save if no input field is being edited
        if (!document.activeElement || 
            (document.activeElement.tagName !== 'INPUT' && 
             document.activeElement.tagName !== 'TEXTAREA')) {
          onSave();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    
    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onSave]);

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  return (
    <header className="sticky top-0 z-10 bg-background border-b px-4 py-3 flex items-center justify-between shadow-sm">
      <div className="flex items-center gap-2">
        <div className="flex items-center justify-center rounded text-primary-foreground w-7 h-7 sm:w-10 sm:h-10">
          <img 
            src="/app-favicon.svg" 
            alt="Journey Map Logo" 
            className="w-7 h-7 sm:w-10 sm:h-10"
          />
        </div>
        <div className="flex flex-col items-start max-w-[200px] sm:max-w-none">
          <h1 className="text-sm sm:text-3xl font-bold text-primary font-handwritten whitespace-nowrap tracking-tight leading-tight">User Journey Map Notebook</h1>
          <span className="text-[8px] sm:text-xs italic text-primary/70 -mt-0.5">by 3a Hon Ji Zen</span>
        </div>
      </div>
      
      <div className="flex items-center gap-1 sm:gap-2">
        {isMobile ? (
          <>
            {/* Mobile view with New Journey button and hamburger menu */}
            <div className="flex items-center ml-1">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onCreateNew}
                className={cn("text-primary border-primary hover:bg-primary/10 hover:text-primary font-medium")}
              >
                <Plus className="size-4 mr-1" />
                <span className="hidden sm:inline">New Journey</span>
              </Button>
              
              <NotebookMenu
                onSave={onSave}
                setStages={setStages}
                setJourney={setJourney}
                stages={stages}
                journey={journey}
                isMobile={true}
              />
              
              {/* Mobile hamburger menu for all other options */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="h-8 w-8 ml-1.5">
                    <Menu className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={onImport}>
                    <Download className="mr-2 h-4 w-4" />
                    <span>Import</span>
                  </DropdownMenuItem>
                  
                  <DropdownMenuSeparator />
                  
                  <DropdownMenuItem onClick={onExport} disabled className="cursor-not-allowed opacity-50">
                    <Upload className="mr-2 h-4 w-4" />
                    <span>Export as PNG</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onExportPDF} disabled className="cursor-not-allowed opacity-50">
                    <Upload className="mr-2 h-4 w-4" />
                    <span>Export as PDF</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onExportJSON}>
                    <Upload className="mr-2 h-4 w-4" />
                    <span>Export as JSON</span>
                  </DropdownMenuItem>
                  
                  <DropdownMenuSeparator />
                  
                  <DropdownMenuItem onClick={toggleTheme}>
                    {theme === 'dark' ? (
                      <>
                        <Sun className="mr-2 h-4 w-4" />
                        <span>Light Mode</span>
                      </>
                    ) : (
                      <>
                        <Moon className="mr-2 h-4 w-4" />
                        <span>Dark Mode</span>
                      </>
                    )}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </>
        ) : (
          <>
            {/* Desktop view with normal controls */}
            <div className="flex items-center gap-1 mr-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onCreateNew}
                className={cn("text-primary border-primary hover:bg-primary/10 hover:text-primary font-medium")}
              >
                <Plus className="size-4 mr-1" />
                <span className="hidden sm:inline">New Journey</span>
              </Button>
              
              <Button variant="outline" size="sm" onClick={onZoomOut} className="h-8 w-8 px-0">
                <ZoomOut className="size-4" />
              </Button>
              <span className="text-sm font-medium px-2 min-w-10 text-center">
                {Math.round(zoomLevel * 100)}%
              </span>
              <Button variant="outline" size="sm" onClick={onZoomIn} className="h-8 w-8 px-0">
                <ZoomIn className="size-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onReset}
                className={cn("text-muted-foreground hover:text-foreground ml-1")}
              >
                <RefreshCw className="size-4 mr-1" />
                <span className="hidden sm:inline">Reset</span>
              </Button>
            </div>
            
            <NotebookMenu
              onSave={onSave}
              setStages={setStages}
              setJourney={setJourney}
              stages={stages}
              journey={journey}
              isMobile={false}
            />
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={onImport}
                    className={cn("text-muted-foreground hover:text-foreground")}
                  >
                    <Download className="size-4 mr-1" />
                    <span className="hidden sm:inline">Import</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Import a journey map from a JSON file</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className={cn("text-muted-foreground hover:text-foreground cursor-pointer")}
                >
                  <Upload className="size-4 mr-1" />
                  <span className="hidden sm:inline">Export</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem disabled className="cursor-not-allowed opacity-50">
                  <Upload className="size-4 mr-2" />
                  <span>PNG Image</span>
                </DropdownMenuItem>
                <DropdownMenuItem disabled className="cursor-not-allowed opacity-50">
                  <Upload className="size-4 mr-2" />
                  <span>PDF Document</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onExportJSON} className="cursor-pointer">
                  <Upload className="size-4 mr-2" />
                  <span>JSON Data</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={toggleTheme}
                    className="ml-2 h-9 w-9"
                  >
                    {theme === 'dark' ? (
                      <Sun className="h-4 w-4" />
                    ) : (
                      <Moon className="h-4 w-4" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Toggle dark mode</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </>
        )}
      </div>
    </header>
  );
};

export default Header;
